
import React, { useState, useEffect, useMemo } from 'react';
import { HINDI_RECIPES, STATE_HINDI_MAP } from '../hindi-recipes';
import { TAMIL_RECIPES, STATE_TAMIL_MAP } from '../tamil-recipes';
import { TELUGU_RECIPES, STATE_TELUGU_MAP } from '../telugu-recipes';
import { ARABIC_RECIPES, STATE_ARABIC_MAP } from '../arabic-recipes';
import { getRecipeImage } from '../recipe-images';
import {
  getCoreDishTitle,
  getStateName,
  getStateStyleLabel,
  getStateIcon,
  groupRecipesByDish
} from '../recipe-variants';

export default function RecipeExplorer({ t, lang, onOpenRecipeModal }) {
  const [recipes, setRecipes] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [search, setSearch] = useState('');
  const [diet, setDiet] = useState('all');
  const [timing, setTiming] = useState('all');
  const [region, setRegion] = useState('all');
  const [loading, setLoading] = useState(true);

  const groupedDishes = useMemo(() => {
    return groupRecipesByDish(recipes);
  }, [recipes]);

  const isEng = lang === 'English';
  const isTa = lang === 'Tamil';
  const isHi = lang === 'Hindi';
  const isTe = lang === 'Telugu';
  const isKn = lang === 'Kannada';
  const isMl = lang === 'Malayalam';
  const isAr = lang === 'Arabic';

  const STATE_KANNADA_MAP = {
    "Tamil Nadu": "ತಮಿಳುನಾಡು",
    "Kerala": "ಕೇರಳ",
    "Karnataka": "ಕರ್ನಾಟಕ",
    "Andhra Pradesh": "ಆಂಧ್ರಪ್ರದೇಶ",
    "Telangana": "ತೆಲಂಗಾಣ"
  };

  const STATE_MALAYALAM_MAP = {
    "Tamil Nadu": "തമിഴ്‌നാട്",
    "Kerala": "കേരളം",
    "Karnataka": "കർണാടക",
    "Andhra Pradesh": "ആന്ധ്രാപ്രദേശ്",
    "Telangana": "തെലങ്കാന"
  };

  useEffect(() => {
    fetchRecipes();
  }, [search, diet, timing, region]);

  const fetchRecipes = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('q', search);
      if (diet !== 'all') params.append('diet', diet);
      if (timing !== 'all') params.append('timing', timing);
      if (region !== 'all') params.append('region', region);

      const res = await fetch(`/api/recipes?${params.toString()}`);
      const data = await res.json();
      setRecipes(data.recipes || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setLoading(false);
    }
  };

  const handleSendToBot = (recipe) => {
    document.getElementById('aiAssistantSection')?.scrollIntoView({ behavior: 'smooth' });
    const input = document.querySelector('.tg-input-field');
    const sendBtn = document.querySelector('.tg-send-btn');
    if (input && sendBtn) {
      input.value = `/recipe ${recipe.meal}`;
      sendBtn.click();
    }
  };

  const getDifficulty = (r) => {
    if (isEng) return r.difficulty || 'Easy';
    if (isTa) {
      if (r.difficulty === 'Medium') return 'நடுத்தரம்';
      if (r.difficulty === 'Hard') return 'கடினம்';
      return r.difficulty_ta || 'சுலபம்';
    }
    if (isHi) {
      if (r.difficulty === 'Medium') return 'मध्यम';
      if (r.difficulty === 'Hard') return 'कठिन';
      return 'आसान';
    }
    if (isTe) {
      if (r.difficulty === 'Medium') return 'మధ్యస్థం';
      if (r.difficulty === 'Hard') return 'కఠినం';
      return 'సులభం';
    }
    if (isKn) {
      if (r.difficulty === 'Medium') return 'ಮಧ್ಯಮ';
      if (r.difficulty === 'Hard') return 'ಕಠಿಣ';
      return 'ಸುಲಭ';
    }
    if (isMl) {
      if (r.difficulty === 'Medium') return 'ഇടത്തരം';
      if (r.difficulty === 'Hard') return 'കഠിനം';
      return 'എളുപ്പം';
    }
    if (isAr) {
      if (r.difficulty === 'Medium') return 'متوسط';
      if (r.difficulty === 'Hard') return 'صعب';
      return 'سهل';
    }
    return r.difficulty || 'Easy';
  };

  const getCardTitle = (r) => {
    if (isTa) return TAMIL_RECIPES[r.id]?.meal_ta || r.meal_ta || r.meal;
    if (isHi) return HINDI_RECIPES[r.id]?.meal_hi || r.meal;
    if (isTe) return TELUGU_RECIPES[r.id]?.meal_te || r.meal;
    if (isAr) return ARABIC_RECIPES[r.id]?.meal_ar || r.meal;
    return r.meal;
  };

  const getCardDesc = (r) => {
    if (isTa) return TAMIL_RECIPES[r.id]?.desc_ta || r.desc_ta || r.description;
    if (isHi) return HINDI_RECIPES[r.id]?.desc_hi || r.description;
    if (isTe) return TELUGU_RECIPES[r.id]?.desc_te || r.description;
    if (isAr) return ARABIC_RECIPES[r.id]?.desc_ar || r.description;
    return r.description;
  };

  const getCardState = (r) => {
    if (isTa) return STATE_TAMIL_MAP[r.state] || r.state;
    if (isHi) return STATE_HINDI_MAP[r.state] || r.state;
    if (isTe) return STATE_TELUGU_MAP[r.state] || r.state;
    if (isKn) return STATE_KANNADA_MAP[r.state] || r.state;
    if (isMl) return STATE_MALAYALAM_MAP[r.state] || r.state;
    if (isAr) return STATE_ARABIC_MAP[r.state] || r.state;
    return r.state;
  };

  const getComboDishes = (r) => {
    if (isTa && TAMIL_RECIPES[r.id]?.dishes_ta) {
      return TAMIL_RECIPES[r.id].dishes_ta.map(d => d.name_ta || d.name);
    }
    if (isHi && HINDI_RECIPES[r.id]?.dishes_hi) {
      return HINDI_RECIPES[r.id].dishes_hi.map(d => d.name_hi || d.name);
    }
    if (isTe && TELUGU_RECIPES[r.id]?.dishes_te) {
      return TELUGU_RECIPES[r.id].dishes_te.map(d => d.name_te || d.name);
    }
    if (isAr && ARABIC_RECIPES[r.id]?.dishes_ar) {
      return ARABIC_RECIPES[r.id].dishes_ar.map(d => d.name_ar || d.name);
    }
    if (r.dishes && r.dishes.length > 0) {
      return r.dishes.map(d => d.name);
    }
    return (r.meal || '').split('+').map(s => s.trim()).filter(Boolean);
  };

  const getIngredientsPreview = (r) => {
    if (isTe && TELUGU_RECIPES[r.id]?.dishes_te) {
      const teIngs = TELUGU_RECIPES[r.id].dishes_te.flatMap(d => (d.ingredients_te || []).map(i => i[0]));
      if (teIngs.length > 0) return teIngs.slice(0, 4).join(', ');
    }
    if (isAr && ARABIC_RECIPES[r.id]?.dishes_ar) {
      const arIngs = ARABIC_RECIPES[r.id].dishes_ar.flatMap(d => (d.ingredients_ar || []).map(i => i[0]));
      if (arIngs.length > 0) return arIngs.slice(0, 4).join('، ');
    }
    if (isHi && HINDI_RECIPES[r.id]?.dishes_hi) {
      const hiIngs = HINDI_RECIPES[r.id].dishes_hi.flatMap(d => (d.ingredients_hi || []).map(i => i[0]));
      if (hiIngs.length > 0) return hiIngs.slice(0, 4).join(', ');
    }
    if (isTa) {
      const taDishes = TAMIL_RECIPES[r.id]?.dishes_ta || r.dishes || [];
      const taIngs = taDishes.flatMap(d => (d.ingredients_ta || []).map(i => i[0]));
      if (taIngs.length > 0) return taIngs.slice(0, 4).join(', ');
    }
    if (r.all_ingredients && r.all_ingredients.length > 0) {
      return r.all_ingredients.slice(0, 4).join(', ');
    }
    const dIngs = (r.dishes || []).flatMap(d => (d.ingredients || []).map(i => i[0]));
    return dIngs.slice(0, 4).join(', ');
  };

  return (
    <section id="recipesSection" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t.navRecipes}</span>
          <h2 className="section-title">{t.recipeTitle}</h2>
          <p className="section-desc">{t.recipeSubtitle}</p>
        </div>

        <div className="recipe-controls">
          <div className="recipe-search-bar">
            <svg className="search-icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              className="recipe-search-input"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* 5 Regional South Indian Categories */}
          <div className="regional-category-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <button
              type="button"
              className={`filter-pill ${region === 'all' ? 'active' : ''}`}
              onClick={() => setRegion('all')}
              style={{ fontWeight: 600 }}
            >
              {t.regionAll}
            </button>
            <button
              type="button"
              className={`filter-pill ${region === 'Tamil Nadu' ? 'active' : ''}`}
              onClick={() => setRegion('Tamil Nadu')}
              style={{ fontWeight: 600 }}
            >
              {t.regionTN}
            </button>
            <button
              type="button"
              className={`filter-pill ${region === 'Kerala' ? 'active' : ''}`}
              onClick={() => setRegion('Kerala')}
              style={{ fontWeight: 600 }}
            >
              {t.regionKerala}
            </button>
            <button
              type="button"
              className={`filter-pill ${region === 'Karnataka' ? 'active' : ''}`}
              onClick={() => setRegion('Karnataka')}
              style={{ fontWeight: 600 }}
            >
              {t.regionKarnataka}
            </button>
            <button
              type="button"
              className={`filter-pill ${region === 'Andhra Pradesh' ? 'active' : ''}`}
              onClick={() => setRegion('Andhra Pradesh')}
              style={{ fontWeight: 600 }}
            >
              {t.regionAndhra}
            </button>
            <button
              type="button"
              className={`filter-pill ${region === 'Telangana' ? 'active' : ''}`}
              onClick={() => setRegion('Telangana')}
              style={{ fontWeight: 600 }}
            >
              {t.regionTelangana}
            </button>
          </div>

          <div className="filters-row">
            <div className="pill-group">
              <button
                type="button"
                className={`filter-pill ${diet === 'all' ? 'active' : ''}`}
                onClick={() => setDiet('all')}
              >
                {t.filterAllDiets}
              </button>
              <button
                type="button"
                className={`filter-pill ${diet === 'Vegetarian' ? 'active' : ''}`}
                onClick={() => setDiet('Vegetarian')}
              >
                {t.filterVeg}
              </button>
              <button
                type="button"
                className={`filter-pill ${diet === 'Non-Vegetarian' ? 'active' : ''}`}
                onClick={() => setDiet('Non-Vegetarian')}
              >
                {t.filterNonVeg}
              </button>
            </div>

            <div className="pill-group">
              <button
                type="button"
                className={`filter-pill ${timing === 'all' ? 'active' : ''}`}
                onClick={() => setTiming('all')}
              >
                {t.filterAllTimings}
              </button>
              <button
                type="button"
                className={`filter-pill ${timing === 'Breakfast' ? 'active' : ''}`}
                onClick={() => setTiming('Breakfast')}
              >
                {t.filterBreakfast}
              </button>
              <button
                type="button"
                className={`filter-pill ${timing === 'Lunch' ? 'active' : ''}`}
                onClick={() => setTiming('Lunch')}
              >
                {t.filterLunch}
              </button>
              <button
                type="button"
                className={`filter-pill ${timing === 'Dinner' ? 'active' : ''}`}
                onClick={() => setTiming('Dinner')}
              >
                {t.filterDinner}
              </button>
              <button
                type="button"
                className={`filter-pill ${timing === 'Quick' ? 'active' : ''}`}
                onClick={() => setTiming('Quick')}
              >
                {t.filterQuick}
              </button>
            </div>

            <select
              className="region-dropdown"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              <option value="all">{t.regionAll}</option>
              <option value="Tamil Nadu">{t.regionTN}</option>
              <option value="Kerala">{t.regionKerala}</option>
              <option value="Karnataka">{t.regionKarnataka}</option>
              <option value="Andhra Pradesh">{t.regionAndhra}</option>
              <option value="Telangana">{t.regionTelangana}</option>
            </select>
          </div>

          <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>
            {loading ? t.recipesSearching : (
              isTa
                ? `${groupedDishes.length} பிரதான உணவுகள் (${recipes.length} பிராந்திய செய்முறைகள்)`
                : (isHi
                  ? `${groupedDishes.length} मुख्य व्यंजन (${recipes.length} क्षेत्रीय शैलियाँ)`
                  : (isTe
                    ? `${groupedDishes.length} ప్రధాన వంటకాలు (${recipes.length} ప్రాంతీయ శైలులు)`
                    : `${groupedDishes.length} Dishes (${recipes.length} Regional Variations)`))
            )}
          </div>
        </div>

        {groupedDishes.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-icon">🍲</div>
            <h3>{t.emptyRecipesTitle}</h3>
            <p>{t.emptyRecipesDesc}</p>
          </div>
        ) : (
          <div className="recipes-grid">
            {groupedDishes.map(group => {
              // Determine active variant for this dish
              let activeVariant = group.variants.find(v => v.id === selectedVariants[group.coreDish]);
              if (!activeVariant) {
                if (region !== 'all') {
                  activeVariant = group.variants.find(v => v.state === region);
                }
                if (!activeVariant) {
                  activeVariant = group.variants[0];
                }
              }

              const r = activeVariant;
              const dietBadgeClass = r.diet === 'Vegetarian' ? 'badge-veg' : 'badge-nonveg';
              const ingPreview = getIngredientsPreview(r);
              const cardTitle = getCardTitle(r);
              const coreDishTitle = getCoreDishTitle(group.coreDish, lang);
              const cardDesc = getCardDesc(r);
              const recipeImg = getRecipeImage(r);

              return (
                <div key={group.coreDish} className="recipe-card">
                  {/* Front Recipe Image */}
                  <div
                    className="recipe-card-media"
                    onClick={() => onOpenRecipeModal(r, 4, group.variants)}
                    title={`${coreDishTitle} - ${t.btnRecipeAndPortions || 'View Recipe'}`}
                  >
                    <img
                      src={recipeImg}
                      alt={coreDishTitle}
                      className="recipe-card-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `/images/recipes/recipe_${r.id || 1}.jpg`;
                      }}
                    />
                    <div className="recipe-media-gradient"></div>
                    <div className="recipe-media-badges">
                      <span className={`badge ${dietBadgeClass}`}>
                        <span className="diet-dot"></span> {r.diet === 'Vegetarian' ? t.cardVegBadge : t.cardNonVegBadge}
                      </span>
                      <span className="badge badge-timing">⏱ {r.minutes} {t.cardMins}</span>
                    </div>
                    <span className="recipe-media-cuisine">📍 {getStateStyleLabel(r.state, lang)}</span>
                  </div>

                  <div className="recipe-card-header">
                    <h3 className="recipe-card-title">{coreDishTitle}</h3>
                    <div className="card-dish-sub">
                      <span>{getStateIcon(r.state)} {cardTitle}</span>
                    </div>

                    {/* Regional Style Dropdown Selector */}
                    {group.variants.length > 1 && (
                      <div className="card-style-selector-row" onClick={(e) => e.stopPropagation()}>
                        <label className="card-style-selector-label">
                          <span className="style-selector-icon">📍</span>
                          <span>
                            {isTa ? 'பிராந்திய ஸ்டைல்:' : (isHi ? 'क्षेत्रीय शैली:' : (isTe ? 'ప్రాంతీయ శైలి:' : 'Regional Style:'))}
                          </span>
                        </label>
                        <select
                          className="card-style-dropdown"
                          value={r.id}
                          onChange={(e) => {
                            const newId = Number(e.target.value);
                            setSelectedVariants(prev => ({ ...prev, [group.coreDish]: newId }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {group.variants.map(v => (
                            <option key={v.id} value={v.id}>
                              {getStateIcon(v.state)} {getStateStyleLabel(v.state, lang)} ({v.state})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Combination Breakdown Badges */}
                    <div className="recipe-combo-row">
                      <span className="combo-row-label">
                        {isTa ? '🍽️ கூட்டு உணவுகள்:' : (isHi ? '🍽️ कॉम्बो थाली:' : (isTe ? '🍽️ కాంబో వంటకాలు:' : (isAr ? '🍽️ تشكيلة الوجبة:' : '🍽️ Meal Combo:')))}
                      </span>
                      <div className="combo-row-chips">
                        {getComboDishes(r).map((dishName, dIdx) => (
                          <span key={dIdx} className="combo-dish-chip">
                            {dishName}
                          </span>
                        ))}
                      </div>
                    </div>

                    <p className="recipe-card-desc">{cardDesc}</p>
                  </div>

                  <div className="recipe-card-body">
                    <div className="ing-preview">
                      <span className="ing-label">{t.cardKeyIngs}</span>
                      <span className="ing-tags">{ingPreview ? `${ingPreview}...` : ''}</span>
                    </div>
                    <div className="card-meta-row">
                      <span className="meta-tag">{t.cardScales}</span>
                      <span className="meta-tag">⚡ {getDifficulty(r)}</span>
                    </div>
                  </div>

                  <div className="recipe-card-footer">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => onOpenRecipeModal(r, 4, group.variants)}
                    >
                      <span>{t.btnRecipeAndPortions}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => handleSendToBot(r)}
                      title={t.btnAskChefTitle}
                    >
                      <span>{t.btnAskChef}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
